import type {ReactElement} from 'react';
import React, { useEffect, useState} from 'react';
import {SliderOptions} from '@Components/slider-options';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {getUniqueString} from '@Utils/string.util';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch} from '@Components/toggle-switch';
import {ControlledList} from '@Components/controlled-list';
import type { ControlledListItem3} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import Divider from '@Components/divider';
import {Label} from '@Components/label';
import {InputField} from '@Components/input-field-v2';
import {InputFieldState} from '@Components/input-field-v2/input-field.types';
import {centimeterToInch, feetToInch, inchToCentimeter, inchToFeet, inchToMillimeter, inchToPixel, millimeterToInch, pixelToInch} from '@Utils/math.util';
import {isUserLoggedIn} from '@Libraries/user.library';
import type {NewSizeParams} from './new-size-section.types';
import styles from './new-size-section.module.scss';
import {SizeUnits, DIMENSION_RANGE} from '@/poster-whiteboard/libraries/resize-poster.library';

export interface NewSizeSectionProps extends NewSizeParams {
  title?: string;
  className?: string;
  onParamsChanged?: (params: NewSizeParams) => void;
  allowSaveCustomSize: boolean;
}

const SIZE_DROPDOWN_OPTIONS: Array<SizeUnits> = [SizeUnits.PX, SizeUnits.IN, SizeUnits.MM, SizeUnits.CM, SizeUnits.FT];
const DEFAULT_UNITS: SizeUnits = SIZE_DROPDOWN_OPTIONS[0];
const DEFAULT_WIDTH = DIMENSION_RANGE[DEFAULT_UNITS].min;
const DEFAULT_HEIGHT = DIMENSION_RANGE[DEFAULT_UNITS].min;
const DEFAULT_IS_SAVED = false;
const CUSTOM_POSTER_TYPE_NAME_PLACEHOLDER = `For example, Square ${DEFAULT_WIDTH}x${DEFAULT_HEIGHT}`;
const DEFAULT_NAME = '';
const UNIT_TO_STEP_MAPPING = {
  [SizeUnits.PX]: 1,
  [SizeUnits.IN]: 0.01,
  [SizeUnits.MM]: 1,
  [SizeUnits.CM]: 0.1,
  [SizeUnits.FT]: 0.01,
};

function NewSizeSection({className = '', onParamsChanged = $.noop, ...props}: NewSizeSectionProps): ReactElement {
  const [width, setWidth] = useState<number>(props.width ?? DEFAULT_WIDTH);
  const [height, setHeight] = useState<number>(props.height ?? DEFAULT_HEIGHT);
  const [isSaved, setIsSaved] = useState(props.isSaved ?? DEFAULT_IS_SAVED);
  const [units, setUnits] = useState<SizeUnits>(props.units ?? DEFAULT_UNITS);
  const [name, setName] = useState<string>(props.name ?? DEFAULT_NAME);
  const [inputFieldState, setInputFieldState] = useState<InputFieldState>(InputFieldState.DEFAULT);
  let dropDownListIdToSizeUnitMapping: Record<string, SizeUnits> = {};
  const id = getUniqueString();

  useEffect(() => {
    onParamsChanged({
      width,
      height,
      units,
      isSaved,
      name,
    });
  }, [width, height, units, isSaved, name]);

  const getDropDownListItems = (): Array<ControlledListItem3> => {
    return SIZE_DROPDOWN_OPTIONS.map((sizeUnit, index) => {
      return {
        id: `${id}-dropdown-opt-${index + 1}`,
        text: `${sizeUnit}`,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        isSelected: units === sizeUnit,
      } as ControlledListItem3;
    });
  };

  const getDropDownList = (): ReactElement => {
    const dropDownItems: Array<ControlledListItem3> = getDropDownListItems();
    dropDownListIdToSizeUnitMapping = {};
    dropDownItems.forEach((dropDownListItem) => {
      dropDownListIdToSizeUnitMapping[dropDownListItem.id] = dropDownListItem.text as SizeUnits;
    });

    return (
      <ControlledList
        className={styles.dropdownList}
        id={`${id}-dropdown-list`}
        onClick={(listItemId): void => {
          setUnits((currentUnit) => {
            const proposedUnits = dropDownListIdToSizeUnitMapping[listItemId];
            if (proposedUnits === props.units && props.width && props.height) {
              setWidth(props.width);
              setHeight(props.height);
            } else {
              const newWidth = getConvertedDimension(width, currentUnit, proposedUnits);
              const newHeight = getConvertedDimension(height, currentUnit, proposedUnits);
              setWidth(newWidth);
              setHeight(newHeight);
            }
            return proposedUnits;
          });
        }}
        items={dropDownItems}
      />
    );
  };

  const getConvertedDimension = (number: number, from: SizeUnits, to: SizeUnits): number => {
    if (from === to) {
      return number;
    }

    let toInches = number;

    switch (from) {
      case SizeUnits.IN:
        break;
      case SizeUnits.FT:
        toInches = feetToInch(number);
        break;
      case SizeUnits.CM:
        toInches = centimeterToInch(number);
        break;
      case SizeUnits.MM:
        toInches = millimeterToInch(number);
        break;
      case SizeUnits.PX:
        toInches = pixelToInch(number);
        break;
      default:
        throw new Error('Unhandled unit');
    }

    switch (to) {
      case SizeUnits.IN:
        return toInches;
      case SizeUnits.FT:
        return inchToFeet(toInches);
      case SizeUnits.CM:
        return inchToCentimeter(toInches);
      case SizeUnits.MM:
        return inchToMillimeter(toInches);
      case SizeUnits.PX:
        return inchToPixel(toInches);
      default:
        throw new Error('Unhandled unit');
    }
  };

  const getLabelText = (text: string): ReactElement => {
    return <Text val={text} size={TextSize.XSMALL} className={`${styles.text} spacing-m-r-5`} />;
  };

  const getUnitsRow = (): ReactElement => {
    return (
      <div className="flex-row-justify-between flex-items-center spacing-m-t-4">
        {getLabelText(window.i18next.t('pmwjs_units'))}
        <Dropdown
          position={DROPDOWN_POSITION.FIXED}
          alignment={ALIGNMENT_TYPE.BOTTOM_END}
          popUpHasCustomWidth
          className={styles.dropdown}
          selector={<DropdownDefaultSelectBox id={`${id}-dropdown`} text={units} size={DROPDOWN_SIZE.SMALL} isLoading={false} />}
          popup={getDropDownList()}
          popupSpacingClass="spacing-p-b-0 spacing-p-r-2 spacing-p-l-2 spacing-p-t-2"
        />
      </div>
    );
  };

  const getSaveCustomSizeContainer = (): ReactElement => {
    return (
      <div className="spacing-m-t-2 flex-v-row">
        <Divider />
        <div className={`flex-v-row spacing-p-3 ${styles.saveCustomTypeNameContainer}`}>
          <Label text={window.i18next.t('pmwjs_name')} isBold />
          <InputField
            className="spacing-m-t-2"
            placeholder={CUSTOM_POSTER_TYPE_NAME_PLACEHOLDER}
            onInputChange={(value): void => {
              if (typeof value === 'string') {
                setName(value);
                if (value.length <= 0 || value.trim().length === 0) {
                  setInputFieldState(InputFieldState.ERROR);
                } else {
                  setInputFieldState(InputFieldState.SUCCESS);
                }
              }
            }}
            input={name}
            state={inputFieldState}
          />
        </div>
      </div>
    );
  };

  const getSaveRow = (): ReactElement => {
    return (
      <div className="flex-v-row">
        <div className="flex-row-justify-between flex-items-center spacing-m-t-4">
          {getLabelText(window.i18next.t('pmwjs_save_for_reuse'))}
          <ToggleSwitch
            id={`${id}-toggle`}
            isChecked={isSaved}
            handleToggle={(): void => {
              setIsSaved(!isSaved);
            }}
          />
        </div>
        {isSaved ? getSaveCustomSizeContainer() : null}
      </div>
    );
  };

  const getContent = (): ReactElement => {
    return (
      <div className={`${styles.panel} ${className} _full-width _full-height`}>
        <SliderOptions
          id={`new-size-width-${units}`}
          range={{
            min: DIMENSION_RANGE[units].min,
            max: DIMENSION_RANGE[units].max,
          }}
          transformDisplayTo={{
            min: DIMENSION_RANGE[units].min,
            max: DIMENSION_RANGE[units].max,
          }}
          className="spacing-m-t-0"
          value={width}
          onChange={setWidth}
          label={window.i18next.t('pmwjs_width')}
          minimumStepFromInputField={UNIT_TO_STEP_MAPPING[units]}
          precision={UNIT_TO_STEP_MAPPING[units]}
          step={1}
          typingStoppedTimeout={1500}
        />
        <SliderOptions
          id={`new-size-height-${units}`}
          range={{
            min: DIMENSION_RANGE[units].min,
            max: DIMENSION_RANGE[units].max,
          }}
          transformDisplayTo={{
            min: DIMENSION_RANGE[units].min,
            max: DIMENSION_RANGE[units].max,
          }}
          className="spacing-m-t-4"
          value={height}
          onChange={setHeight}
          label={window.i18next.t('pmwjs_height')}
          minimumStepFromInputField={UNIT_TO_STEP_MAPPING[units]}
          precision={UNIT_TO_STEP_MAPPING[units]}
          step={1}
          typingStoppedTimeout={1500}
        />
        {getUnitsRow()}
        {props.allowSaveCustomSize && isUserLoggedIn() ? getSaveRow() : null}
      </div>
    );
  };

  return getContent();
}

export default NewSizeSection;
